import { CircularProgress } from '@mui/material';
import { cn } from 'cn';
import { useEffect, useState } from 'react';

type P = {
  readonly alignLeft?: boolean;
  readonly color?: string;
  readonly initialDelay?: number;
  readonly size?: number;
  readonly title?: string;
} & ComponentStyling;

/**
 * Component that displays a loading indicator.
 *
 * @param alignLeft - If true, aligns the spinner to the left.
 * @param color - The color of the spinner.
 * @param initialDelay - Delay in milliseconds before the spinner is shown. 500 is a good value to avoid flickering for fast actions.
 * @param size - The size of the spinner.
 * @param title - Optional title to display next to the spinner.
 *
 * @returns The spinner element or null if hidden.
 */
export const Spinner = ({
  alignLeft,
  className,
  color,
  initialDelay,
  size = 20,
  style,
  title,
}: P) => {
  const [isHidden, setIsHidden] = useState(
    typeof initialDelay === 'number' && initialDelay > 0,
  );

  useEffect(() => {
    if (typeof initialDelay === 'number' && initialDelay > 0) {
      const timeout = setTimeout(() => {
        setIsHidden(false);
      }, initialDelay);

      return () => {
        clearTimeout(timeout);
      };
    }
  }, [initialDelay]);

  return (
    <div
      className={cn([
        'h-full transition-opacity duration-200 ease-out',
        {
          'flex w-full items-center justify-center': !alignLeft,
          'opacity-0': isHidden,
          'text-inherit': color === 'inherit',
          'text-white': color === 'white',
        },
        className,
      ])}
      style={style}
    >
      <CircularProgress size={size} style={color ? { color } : undefined} />
      {title ? <div className="ml-2 text-sm">{title}</div> : null}
    </div>
  );
};
